import { apiClient } from '@app/api';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { toTimestamp } from '@mi-front/core';
import { StreamEl } from '@mi-front/ui';
import { useQuery } from '@tanstack/react-query';
import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useStreamSegmentsRange } from '../../../hooks/segments/useStreamSegmentsQuery';
import { useStreamSettingsStore } from '../../../stores/stream-screen-settings.store';

type ChannelData = {
  [key: string]: number;
};
type YoutubeStats = {
  time: string;
  channelId: ChannelData[];
}[];
export function YoutubeStatsWaveformGraph() {
  const station = useActiveStation();
  const auditSettings = useStreamSettingsStore((state) => state.audit);

  const range = useStreamSegmentsRange();

  const { data: youtubeStats, isLoading } = useQuery({
    queryKey: ['youtube-stats', station.id, range.from, range.to],
    queryFn: () => apiClient.audit.fetchYoutubeStats(station.youtubeChannelId!, range),
    enabled: auditSettings.ytstats && !!station.youtubeChannelId && !!range,
  });
  const svgRef = useRef<SVGSVGElement>(null);

  return station.youtubeChannelId && !isLoading ? (
    <StreamEl
      key={'yt-stats'}
      ts={toTimestamp(range.from)}
      position="bottom"
      fullHeight
      style={{
        pointerEvents: 'none',
      }}
    >
      {(scale) => {
        const startX = scale(toTimestamp(range.from)) || 0;
        const endX = scale(toTimestamp(range.to)) || 0;
        const width = startX - endX;

        return (
          <ViewerCountLine
            youtubeStats={youtubeStats}
            width={Math.abs(width)}
            height={200}
            scale={scale}
            svgRef={svgRef}
            station={station}
            range={range}
          />
        );
      }}
    </StreamEl>
  ) : null;
}

interface ViewerCountLineProps {
  youtubeStats: YoutubeStats;
  width: number;
  height: number;
  scale: (ts: number) => number | undefined;
  svgRef: React.RefObject<SVGSVGElement>;
  station: any;
  range: { from: number; to: number };
}

function ViewerCountLine({
  youtubeStats,
  width,
  height,
  scale,
  svgRef,
  station,
  range,
}: ViewerCountLineProps) {
  const { formatMessage } = useIntl();
  useEffect(() => {
    if (!svgRef.current || youtubeStats.length === 0) return;

    if (!station?.youtubeChannelId) {
      return;
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const processedData = youtubeStats.map((stat, index) => {
      const timestamp = toTimestamp(stat.time);

      const stationViewers = stat.channelId.reduce((sum, channel) => {
        if (channel[station.youtubeChannelId]) {
          return sum + channel[station.youtubeChannelId];
        }
        return sum;
      }, 0);

      const result = {
        timestamp,
        viewers: stationViewers || 0,
        time: stat.time,
      };

      return result;
    });

    if (processedData.length === 0 || processedData.every((d) => d.viewers === 0)) {
      return;
    }

    const yScale = d3
      .scaleLinear()
      .domain(d3.extent(processedData, (d) => d.viewers) as [number, number])
      .range([height - 20, 20]);

    // Calculate x positions dynamically
    const getX = (d: (typeof processedData)[0]) => scale(d.timestamp) || 0;

    const streamStartTimestamp = toTimestamp(range.from);
    const xOffset = scale(streamStartTimestamp) || 0;
    const adjustedX = (x: number) => x - xOffset;

    const line = d3
      .line<(typeof processedData)[0]>()
      .x((d) => adjustedX(getX(d)))
      .y((d) => yScale(d.viewers) || 0)
      .curve(d3.curveMonotoneX);

    const area = d3
      .area<(typeof processedData)[0]>()
      .x((d) => adjustedX(getX(d)))
      .y0(height - 20)
      .y1((d) => yScale(d.viewers) || 0)
      .curve(d3.curveMonotoneX);

    svg.append('path').datum(processedData).attr('fill', 'transparent').attr('d', area);

    svg
      .append('path')
      .datum(processedData)
      .attr('fill', 'none')
      .attr('stroke', '#ff6384')
      .attr('stroke-width', 2)
      .attr('d', line);

    svg
      .selectAll('.dot')
      .data(processedData)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('cx', (d) => adjustedX(getX(d)))
      .attr('cy', (d) => yScale(d.viewers) || 0)
      .attr('r', 4)
      .attr('fill', '#ff6384')
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .style('pointer-events', 'all')
      .on('mouseenter', function (event, d) {
        const actualData = event;
        if (!actualData || typeof actualData.viewers !== 'number') {
          return;
        }

        // dot's position to center the tooltip above it
        const dotX = adjustedX(getX(actualData));
        const dotY = yScale(actualData.viewers);

        // get the SVG's position relative to the viewport
        const svgRect = svgRef.current?.getBoundingClientRect();
        const tooltipX = (svgRect?.left || 0) + (dotX ?? 0);
        const tooltipY = (svgRect?.top || 0) + (dotY ?? 0) - 10;

        // Create a new tooltip for each hover to avoid conflicts
        const tooltip = d3
          .select('body')
          .append('div')
          .attr('class', 'hover-tooltip')
          .style('position', 'fixed')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px 12px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('font-weight', 'normal')
          .style('font-family', 'Arial, sans-serif')
          .style('z-index', '999999')
          .style('border', '1px solid #ccc')
          .style('box-shadow', '0 2px 8px rgba(0, 0, 0, 0.3)')
          .style('pointer-events', 'none')
          .style('opacity', 1)
          .style('display', 'block')
          .style('white-space', 'nowrap')
          .html(
            `Viewers: ${(actualData.viewers || 0).toLocaleString()}<br/>${formatMessage({
              id: 'common.text.time',
              defaultMessage: 'Hora',
            })}: ${new Date(actualData.time || Date.now()).toLocaleTimeString()}`,
          )
          .style('left', tooltipX + 'px')
          .style('top', tooltipY + 'px')
          .style('transform', 'translate(-50%, -100%)');
      })
      .on('mouseleave', function () {
        d3.select('body').selectAll('.hover-tooltip').remove();
      });

    // Cleanup
    return () => {
      d3.select('body').selectAll('.hover-tooltip').remove();
    };
  }, [youtubeStats, width, height, scale, svgRef, station]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
